import { recordConsoleError } from './feedbackContext';
import type { FeedbackTag } from '../api/feedbackApi';

/**
 * Hand-off for turning a crash into a reported finding.
 *
 * When an ErrorBoundary trips, the React tree below it is gone — at the app level, that includes
 * the "Report a problem" button itself. So the prefill is parked in sessionStorage and picked up
 * after the page recovers, rather than passed through React state that may no longer exist.
 *
 * We deliberately do NOT auto-post crashes to the Cliq channel. That channel is social: testers
 * watch each other's findings and learn the one-line format from them. A stack-trace firehose
 * would bury the thing that actually matters — a dietary tester writing "puree resident's ticket
 * printed the regular texture" — and one render loop can out-post a week of real findings.
 * Errors are already attached to every submission, and App Insights owns the raw stream.
 */

const STORAGE_KEY = 'dietech_feedback_prefill';

export interface FeedbackPrefill {
  tag: FeedbackTag;
  severity: number;
  summary: string;
  /** The page that crashed (its pathname): the report opens there, or on the dashboard the error screen sends testers to. */
  path?: string;
}

/**
 * Parks a prefill and records the error into the context buffer, so the finding carries the
 * technical detail even though the tester only writes a sentence.
 */
export function requestCrashReport(error: Error, label: string, componentStack?: string) {
  recordConsoleError(`[ErrorBoundary:${label}] ${error.message}`, componentStack ?? error.stack);

  const path = currentPath();
  const prefill: FeedbackPrefill = {
    tag: 'BROKEN',
    // A crash always stopped them — the screen is gone.
    severity: 1,
    // The page is named in the summary: the report may be sent from another page.
    summary: `The ${label} screen${path ? ` (${path})` : ''} crashed: ${error.message}`.slice(0, 500),
    ...(path ? { path } : {}),
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prefill));
  } catch {
    // Private browsing or a full quota — the tester can still report manually.
  }
}

/** Reads and clears any parked prefill. Returns null when there is nothing pending. */
export function consumeCrashReport(): FeedbackPrefill | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);

    const parsed = JSON.parse(raw) as Partial<FeedbackPrefill>;
    if (!parsed?.summary) return null;

    return {
      tag: (parsed.tag ?? 'BROKEN') as FeedbackTag,
      severity: typeof parsed.severity === 'number' ? parsed.severity : 1,
      summary: parsed.summary,
      ...(typeof parsed.path === 'string' ? { path: parsed.path } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Whether a parked report belongs on this page: the page that crashed, or the dashboard,
 * where the error screen's Report This Problem button sends the tester. A report parked
 * without a page (from before the page was kept) opens anywhere, as it always did.
 * Elsewhere it stays parked: it used to open over whatever page loaded next, asking about
 * one the tester had already left (and raising the phone keyboard as it did).
 */
export function crashReportBelongsHere(pathname: string): boolean {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const path = (JSON.parse(raw) as Partial<FeedbackPrefill>)?.path;
    return typeof path !== 'string' || path === pathname || pathname === '/dashboard';
  } catch {
    // A corrupted payload: consumeCrashReport reads it, clears it and returns null.
    return true;
  }
}

function currentPath(): string {
  try {
    return window.location.pathname;
  } catch {
    return '';
  }
}

/** True when a crash report is waiting to be picked up. */
export function hasPendingCrashReport(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}
